import { useState } from 'react';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import type { ReactNode } from 'react';

interface ListFiltersProps {
  /** Contenu des filtres */
  children: ReactNode;
  /** Nombre de filtres actifs */
  activeFiltersCount?: number;
  /** Handler de reset */
  onReset?: () => void;
  /** Label du bouton reset */
  resetLabel?: string;
  /** Recherche (toujours visible, même en mobile) */
  search?: ReactNode;
  /** Filtres avancés (collapsibles en mobile) */
  advancedFilters?: ReactNode;
}

export function ListFilters({
  children,
  activeFiltersCount = 0,
  onReset,
  resetLabel = 'Réinitialiser les filtres',
  search,
  advancedFilters,
}: ListFiltersProps) {
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const hasAdvancedFilters = !!advancedFilters;

  // Legacy mode: no search/advancedFilters split — render children as-is with flex-wrap
  if (!search && !advancedFilters) {
    return (
      <div className="tw-mb-4">
        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3 md:tw-gap-4">
          <div className="tw-flex-1 tw-min-w-0">{children}</div>
          {onReset && (
            <ResetButton
              onReset={onReset}
              activeFiltersCount={activeFiltersCount}
              resetLabel={resetLabel}
            />
          )}
        </div>
      </div>
    );
  }

  // New mode: search + filters on same line (desktop), collapsible filters (mobile)
  return (
    <div className="tw-mb-4">
      {/* Single row: Search + advanced filters inline (desktop) / Search + toggle (mobile) */}
      <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
        {search && <div className="tw-w-full md:tw-w-auto md:tw-min-w-[200px] tw-flex-shrink-0">{search}</div>}

        {/* Mobile toggle button */}
        {hasAdvancedFilters && (
          <button
            type="button"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="tw-flex md:tw-hidden tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors tw-flex-shrink-0"
          >
            <SlidersHorizontal className="tw-w-4 tw-h-4" />
            <span>Filtres</span>
            {activeFiltersCount > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-text-xs tw-font-bold tw-text-white tw-bg-affinicar-primary tw-rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </button>
        )}

        {/* Advanced filters inline on desktop */}
        {hasAdvancedFilters && (
          <div className="tw-hidden md:tw-contents">
            {advancedFilters}
          </div>
        )}

        {onReset && (
          <div className="tw-hidden md:tw-block tw-flex-shrink-0">
            <ResetButton
              onReset={onReset}
              activeFiltersCount={activeFiltersCount}
              resetLabel={resetLabel}
            />
          </div>
        )}
      </div>

      {/* Mobile expanded filters panel */}
      {hasAdvancedFilters && showMobileFilters && (
        <div className="tw-flex md:tw-hidden tw-flex-wrap tw-items-end tw-gap-3 tw-mt-3">
          {advancedFilters}

          {onReset && (
            <div className="tw-flex-shrink-0">
              <ResetButton
                onReset={onReset}
                activeFiltersCount={activeFiltersCount}
                resetLabel={resetLabel}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResetButton({
  onReset,
  activeFiltersCount,
  resetLabel,
}: {
  onReset: () => void;
  activeFiltersCount: number;
  resetLabel: string;
}) {
  return (
    <button
      onClick={onReset}
      disabled={activeFiltersCount === 0}
      className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-1 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
    >
      <RotateCcw className="tw-w-4 tw-h-4" />
      <span>{resetLabel}</span>
      {activeFiltersCount > 0 && (
        <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-text-xs tw-font-bold tw-text-white tw-bg-affinicar-primary tw-rounded-full">
          {activeFiltersCount}
        </span>
      )}
    </button>
  );
}
